import React, { useEffect, useRef, useState } from 'react'

import { Button, Label, TextField, TextLink } from '@octane/spark'

import {
    CancelLinkWrapper,
    ChevronIcon,
    DealOwnerFieldSize,
    DropdownArea,
    ErrorBanner,
    FieldLabel,
    HelperText,
    HorizontalLayout,
    InlineLabel,
    InlineLayout,
    LabelArea,
    NewBadgePin,
    NoMatchesMessage,
    Option,
    Popover,
    SearchInput,
    SearchInputWrapper,
    Trigger,
    TriggerText,
    VerticalLayout,
    Wrapper,
    WriteInForm,
} from './DealOwnerField.styled'
import { DealOwnerUserOption } from './useDealershipUsersWithUsername'

export type DealOwnerFieldLayout = 'horizontal' | 'vertical' | 'inline'

export type DealOwnerFieldProps = {
    value: string
    users: DealOwnerUserOption[]
    disabled?: boolean
    // userId is the DealerUser id for a listed selection; null for write-ins.
    onSave: (newValue: string, userId: number | null) => Promise<void> | void
    label?: string
    helperText?: string
    writeInLabel?: string
    layout?: DealOwnerFieldLayout
    size?: DealOwnerFieldSize
    writeInEnabled?: boolean
    formatOption?: (u: DealOwnerUserOption) => string
    loading?: boolean
    error?: Error | null
    onRetry?: () => void
    // Show a "NEW" pill next to the field label. Opt-in; only used by
    // DealOwnerSection for now (Deal Owner is the newly-introduced feature).
    showNewLabel?: boolean
}

export const formatUserOption = (u: DealOwnerUserOption) => u.name

// Backend delivers names as "Full Name (username)"; Finance Manager stores
// only the bare "Full Name" (no username), so strip the trailing paren group.
export const formatBareName = (u: DealOwnerUserOption) =>
    u.name.replace(/\s*\([^)]*\)\s*$/, '').trim()

export default function DealOwnerField({
    value,
    users,
    disabled = false,
    onSave,
    label = 'Deal Owner',
    helperText = 'Make sure this deal is assigned to the correct owner for tracking.',
    writeInLabel = 'Enter a new Deal Owner',
    layout = 'horizontal',
    size = 'full',
    writeInEnabled = true,
    formatOption = formatUserOption,
    loading = false,
    error = null,
    onRetry,
    showNewLabel = false,
}: DealOwnerFieldProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [mode, setMode] = useState<'list' | 'writein'>('list')
    const [writeInValue, setWriteInValue] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!isOpen) return undefined

        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
                setMode('list')
                setWriteInValue('')
                setSearchQuery('')
            }
        }

        document.addEventListener('mousedown', handleClickOutside)

        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    const closePopover = () => {
        setIsOpen(false)
        setMode('list')
        setWriteInValue('')
        setSearchQuery('')
    }

    // Case-insensitive substring match against the backend-formatted name
    // ("Full Name (username)"). Whitespace collapsed on both sides so
    // "alex   sample" matches "Alex Sample".
    const normalizedQuery = searchQuery
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')

    const filteredUsers = normalizedQuery
        ? users.filter((u) => {
              const haystack = u.name.toLowerCase().replace(/\s+/g, ' ')

              return haystack.includes(normalizedQuery)
          })
        : users

    const handleListSelect = (newValue: string, userId: number | null) => {
        closePopover()
        Promise.resolve(onSave(newValue, userId)).catch(() => {})
    }

    const handleAssignWriteIn = async () => {
        const trimmed = writeInValue.trim()

        if (!trimmed) return

        setIsSaving(true)

        try {
            await onSave(trimmed, null)
            closePopover()
        } catch {
            // Parent surfaces the error via toast; keep popover open for retry.
        } finally {
            setIsSaving(false)
        }
    }

    const toggleOpen = () => {
        if (disabled || loading) return

        setIsOpen((prev) => !prev)
        setMode('list')
        setWriteInValue('')
        setSearchQuery('')
    }

    const labelBlock = (
        <>
            <FieldLabel>{label}</FieldLabel>
            {helperText && helperText.trim() !== '' && (
                <HelperText>{helperText}</HelperText>
            )}
        </>
    )

    const dropdown = (
        <Wrapper ref={wrapperRef} $size={size}>
            {showNewLabel && (
                <NewBadgePin aria-hidden="true">
                    <Label text="NEW" color="spotlight" size="small" />
                </NewBadgePin>
            )}
            <Trigger
                type="button"
                disabled={disabled || loading}
                onClick={toggleOpen}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                data-testid="deal-owner-trigger"
                $size={size}
            >
                <TriggerText $size={size}>{value}</TriggerText>
                <ChevronIcon />
            </Trigger>
            {error && onRetry && (
                <ErrorBanner data-testid="deal-owner-error">
                    Could not load users.{' '}
                    <TextLink
                        as="button"
                        type="button"
                        onClick={onRetry}
                        data-testid="deal-owner-retry"
                    >
                        Retry
                    </TextLink>
                </ErrorBanner>
            )}

            {isOpen && (
                <Popover
                    role={mode === 'list' ? 'listbox' : undefined}
                    data-testid="deal-owner-popover"
                    $size={size}
                >
                    {mode === 'list' && (
                        <>
                            <SearchInputWrapper>
                                <SearchInput
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                    autoFocus
                                    data-testid="deal-owner-search-input"
                                />
                            </SearchInputWrapper>
                            {filteredUsers.map((user) => {
                                const optionText = formatOption(user)

                                return (
                                    <Option
                                        key={user.id}
                                        type="button"
                                        role="option"
                                        title={optionText}
                                        onClick={() =>
                                            handleListSelect(
                                                optionText,
                                                user.id,
                                            )
                                        }
                                        data-testid={`deal-owner-option-${user.id}`}
                                        $size={size}
                                    >
                                        {optionText}
                                    </Option>
                                )
                            })}
                            {filteredUsers.length === 0 && (
                                <NoMatchesMessage data-testid="deal-owner-no-matches">
                                    No matches
                                </NoMatchesMessage>
                            )}
                            {writeInEnabled && (
                                <Option
                                    type="button"
                                    onClick={() => setMode('writein')}
                                    data-testid="deal-owner-writein-trigger"
                                    $size={size}
                                >
                                    Assign to someone not listed
                                </Option>
                            )}
                        </>
                    )}
                    {mode === 'writein' && writeInEnabled && (
                        <WriteInForm>
                            <TextField
                                label={writeInLabel}
                                value={writeInValue}
                                onChange={(v: string) => setWriteInValue(v)}
                                data-testid="deal-owner-writein-input"
                            />
                            <Button
                                fullWidth
                                type="button"
                                onClick={handleAssignWriteIn}
                                disabled={!writeInValue.trim() || isSaving}
                                loading={isSaving}
                                data-testid="deal-owner-writein-assign"
                            >
                                Assign
                            </Button>
                            <CancelLinkWrapper>
                                <TextLink
                                    as="button"
                                    type="button"
                                    onClick={() => {
                                        setMode('list')
                                        setWriteInValue('')
                                    }}
                                    data-testid="deal-owner-writein-cancel"
                                >
                                    Cancel
                                </TextLink>
                            </CancelLinkWrapper>
                        </WriteInForm>
                    )}
                </Popover>
            )}
        </Wrapper>
    )

    if (layout === 'inline') {
        return (
            <InlineLayout data-testid="deal-owner-field">
                <InlineLabel>{label}</InlineLabel>
                {dropdown}
            </InlineLayout>
        )
    }

    if (layout === 'vertical') {
        return (
            <VerticalLayout data-testid="deal-owner-field">
                {labelBlock}
                {dropdown}
            </VerticalLayout>
        )
    }

    return (
        <HorizontalLayout data-testid="deal-owner-field">
            <LabelArea>{labelBlock}</LabelArea>
            <DropdownArea>{dropdown}</DropdownArea>
        </HorizontalLayout>
    )
}
